import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * In-app modal primitive (build-plan §7.9 — NO native popups). A focus-trapped `role="dialog"
 * aria-modal` PORTALLED to `document.body` (the portal dodges the global auto-styler transparency
 * gotcha that strips card chrome on nested content — memory: "Modal w/ table loses background").
 *
 * Behaviour:
 *  - Esc closes (when `onClose` is given) — but a forced modal (no onClose) can't be dismissed by Esc.
 *  - Focus moves into the dialog on open and is restored to the previously-focused element on close.
 *  - Tab/Shift+Tab are trapped within the dialog.
 *  - The backdrop click closes only when dismissible.
 */
export function Modal({
  titleId,
  onClose,
  children,
  dismissible = true,
  wide = false,
}: {
  titleId: string;
  onClose?: () => void;
  children: React.ReactNode;
  dismissible?: boolean;
  /**
   * Un panneau LARGE au lieu des 440 px par défaut (feature 0070 — la bibliothèque d'images). Les
   * dialogues de ce builder posent une question en une phrase et 440 px leur vont ; une GRILLE de
   * vignettes à cette largeur ne montre que deux images par ligne, ce qui transforme « choisis parmi
   * tes images » en défilement. Un drapeau, pas une classe libre : deux largeurs, pas vingt.
   */
  wide?: boolean;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    // Focus the first focusable element (or the panel) so keyboard users land inside the dialog.
    const panel = panelRef.current;
    const first = panel?.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    (first ?? panel)?.focus();

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && dismissible && onClose) {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !panel) return;
      const focusables = Array.from(
        panel.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );
      if (focusables.length === 0) return;
      const firstEl = focusables[0]!;
      const lastEl = focusables[focusables.length - 1]!;
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    }

    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      previouslyFocused.current?.focus?.();
    };
  }, [dismissible, onClose]);

  return createPortal(
    <div
      className="bld-modal__backdrop"
      onMouseDown={(e) => {
        if (dismissible && onClose && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        className={wide ? 'bld-modal bld-modal--wide' : 'bld-modal'}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
